import { apiClient } from './api-client';
import type { FieldDefinition, FieldType, FieldOption, FieldValidation } from '@centraoneplus/shared-types';

export interface CreateFieldDefinitionDto {
  name: string;
  key?: string;
  description?: string;
  fieldType: FieldType;
  options?: FieldOption[];
  defaultValue?: any;
  validation?: FieldValidation;
  placeholder?: string;
  helpText?: string;
  isSystem?: boolean;
  isActive?: boolean;
  // AI Extraction support
  keywords?: string[];
  examples?: string[];
  formFieldConfig?: {
    type?: 'input' | 'dropdown' | 'link' | 'textarea' | 'checkbox';
    subtype?: 'text' | 'number' | 'phone' | 'email' | 'url' | 'date';
    hint?: string;
    maxLength?: number;
    isDisabled?: boolean;
    isDeletable?: boolean;
    accessLevel?: number;
  };
}

export interface UpdateFieldDefinitionDto extends Partial<CreateFieldDefinitionDto> {}

export interface FieldDefinitionFilters {
  isActive?: boolean;
  fieldType?: FieldType;
  isSystem?: boolean;
}

class FieldDefinitionsService {
  private readonly basePath = '/field-definitions';

  async getFieldDefinitions(filters?: FieldDefinitionFilters): Promise<FieldDefinition[]> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.isActive !== undefined) params.append('isActive', String(filters.isActive));
      if (filters.fieldType) params.append('fieldType', filters.fieldType);
      if (filters.isSystem !== undefined) params.append('isSystem', String(filters.isSystem));
    }
    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    const response = await apiClient.get<FieldDefinition[]>(url);
    return response.data;
  }

  async getFieldDefinition(id: string): Promise<FieldDefinition> {
    const response = await apiClient.get<FieldDefinition>(`${this.basePath}/${id}`);
    return response.data;
  }

  async getFieldDefinitionByKey(key: string): Promise<FieldDefinition> {
    const response = await apiClient.get<FieldDefinition>(`${this.basePath}/by-key/${key}`);
    return response.data;
  }

  async createFieldDefinition(dto: CreateFieldDefinitionDto): Promise<FieldDefinition> {
    const response = await apiClient.post<FieldDefinition>(this.basePath, dto);
    return response.data;
  }

  async updateFieldDefinition(id: string, dto: UpdateFieldDefinitionDto): Promise<FieldDefinition> {
    const response = await apiClient.put<FieldDefinition>(`${this.basePath}/${id}`, dto);
    return response.data;
  }

  async deleteFieldDefinition(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }

  async seedSystemFields(): Promise<FieldDefinition[]> {
    const response = await apiClient.post<FieldDefinition[]>(`${this.basePath}/seed-system`);
    return response.data;
  }

  generateKey(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_');
  }
}

export const fieldDefinitionsService = new FieldDefinitionsService();
